import businessScopingData from '../data/businessScoping.json'
import technologyScopingData from '../data/technologyScoping.json'
import riskAppetiteData from '../data/riskAppetite.json'
import maturityAssessmentData from '../data/maturityAssessment.json'

// ============================================================
// Formatting helpers
// ============================================================

export const formatStaffingSize = (value) => {
  if (!value) return 'N/A'
  // Already a display-ready label (contains dash or plus)
  if (value.includes('–') || value.includes('-') || value.includes('+')) {
    return value
  }
  const labels = {
    'small': '1-50',
    'medium': '51-500',
    'large': '501-5,000',
    'enterprise': '5,000+'
  }
  return labels[value] || value
}

export const formatItTeamSize = (value) => {
  if (!value) return 'N/A'
  const labels = {
    '0': 'No dedicated IT',
    '1-5': '1-5 people',
    '6-20': '6-20 people',
    '21-50': '21-50 people',
    '50+': '50+ people'
  }
  return labels[value] || value
}

export const formatGeographicalPresence = (value) => {
  if (!value) return 'N/A'
  const labels = {
    'domestic': 'Domestic Only (US)',
    'international': 'Some International Operations',
    'global': 'Global / Multi-Jurisdiction'
  }
  return labels[value] || value
}

export function relativeTime(dateStr) {
  const now = new Date()
  const date = new Date(dateStr)
  const diffMs = now - date
  const diffMin = Math.floor(diffMs / 60000)
  const diffHr = Math.floor(diffMs / 3600000)
  const diffDay = Math.floor(diffMs / 86400000)

  if (diffMin < 1) return 'just now'
  if (diffMin < 60) return `${diffMin}m ago`
  if (diffHr < 24) return `${diffHr}h ago`
  if (diffDay < 30) return `${diffDay}d ago`
  const months = Math.floor(diffDay / 30)
  return `${months}mo ago`
}

// ============================================================
// Risk Appetite display
// ============================================================

export const getRiskAppetiteDisplay = (score) => {
  if (score === null || score === undefined) return null

  if (score <= 2.0) return {
    label: 'Very Low Tolerance',
    subtitle: 'Conservative posture',
    iconPath: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    gradientFrom: '#22c55e', gradientTo: '#16a34a',
    bgClass: 'bg-green-50 dark:bg-green-900/10',
    textColor: 'text-green-700 dark:text-green-400',
    subtextColor: 'text-green-600 dark:text-green-500'
  }
  if (score <= 2.5) return {
    label: 'Low Risk Tolerance',
    subtitle: 'Conservative posture',
    iconPath: 'M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z',
    gradientFrom: '#22c55e', gradientTo: '#16a34a',
    bgClass: 'bg-green-50 dark:bg-green-900/10',
    textColor: 'text-green-700 dark:text-green-400',
    subtextColor: 'text-green-600 dark:text-green-500'
  }
  if (score <= 3.0) return {
    label: 'Moderate Tolerance',
    subtitle: 'Balanced posture',
    iconPath: 'M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z',
    gradientFrom: '#f59e0b', gradientTo: '#d97706',
    bgClass: 'bg-amber-50 dark:bg-amber-900/10',
    textColor: 'text-amber-700 dark:text-amber-400',
    subtextColor: 'text-amber-600 dark:text-amber-500'
  }
  if (score <= 4.0) return {
    label: 'High Tolerance',
    subtitle: 'Aggressive posture',
    iconPath: 'M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z',
    gradientFrom: '#f97316', gradientTo: '#ea580c',
    bgClass: 'bg-orange-50 dark:bg-orange-900/10',
    textColor: 'text-orange-700 dark:text-orange-400',
    subtextColor: 'text-orange-600 dark:text-orange-500'
  }
  return {
    label: 'Very High Tolerance',
    subtitle: 'Maximum risk acceptance',
    iconPath: 'M13 10V3L4 14h7v7l9-11h-7z',
    gradientFrom: '#ef4444', gradientTo: '#dc2626',
    bgClass: 'bg-red-50 dark:bg-red-900/10',
    textColor: 'text-red-700 dark:text-red-400',
    subtextColor: 'text-red-600 dark:text-red-500'
  }
}

// ============================================================
// Assessment progress calculation
// ============================================================

const getQuestionIds = (data) =>
  data.sections
    ? data.sections.flatMap(s => s.questions)
    : data.questions || []

const isQuestionVisible = (question, responses) => {
  if (!question.showWhen) return true
  const depResponse = responses?.[question.showWhen.questionId]
  const depValue = typeof depResponse === 'object' ? depResponse?.value : depResponse
  return depValue && question.showWhen.values.includes(depValue)
}

const countSectionResponses = (responses, questions) => {
  if (!responses || typeof responses !== 'object') return 0
  return questions.filter(q => {
    const value = responses[q.id]
    if (value === null || value === undefined || value === '') return false
    if (typeof value === 'object' && !value.value && !value.answer) return false
    return true
  }).length
}

export const calculateAssessmentProgress = (assessment) => {
  const responses = assessment.responses || {}

  const bsQuestions = getQuestionIds(businessScopingData).filter(q => isQuestionVisible(q, responses.businessScoping))
  const tsQuestions = getQuestionIds(technologyScopingData)
  const raQuestions = getQuestionIds(riskAppetiteData)
  const maQuestions = getQuestionIds(maturityAssessmentData)

  const businessScopingCount = countSectionResponses(responses.businessScoping, bsQuestions)
  const technologyScopingCount = countSectionResponses(responses.technologyScoping, tsQuestions)
  const riskAppetiteCount = countSectionResponses(responses.riskAppetite, raQuestions)
  const maturityCount = countSectionResponses(responses.maturityAssessment, maQuestions)

  const totalExpected = bsQuestions.length + tsQuestions.length + raQuestions.length +
                        maQuestions.length

  const totalAnswered = businessScopingCount + technologyScopingCount + riskAppetiteCount +
                        maturityCount

  const percent = Math.round((totalAnswered / totalExpected) * 100)

  const currentStep = assessment.currentStep || assessment.current_step || 'business-scoping'
  const currentStepName = currentStep.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')

  return {
    percent: Math.min(percent, 100),
    totalAnswered,
    totalQuestions: totalExpected,
    currentStepName,
    sectionProgress: {
      businessScoping: { answered: businessScopingCount, total: bsQuestions.length },
      technologyScoping: { answered: technologyScopingCount, total: tsQuestions.length },
      riskAppetite: { answered: riskAppetiteCount, total: raQuestions.length },
      maturityAssessment: { answered: maturityCount, total: maQuestions.length }
    }
  }
}

// ============================================================
// Risk Appetite score calculator
// ============================================================

const RISK_APPETITE_QUESTION_COUNT = 10

export const calculateRiskAppetiteScore = (client) => {
  const activeAssessments = (client.assessments || []).filter(a => a.status === 'in_progress')
  const completedAssessments = (client.assessments || [])
    .filter(a => a.status === 'completed')
    .sort((a, b) => new Date(b.completedAt) - new Date(a.completedAt))

  // Check completed assessments first
  if (completedAssessments.length > 0) {
    const score = completedAssessments[0].scores?.riskAppetite?.averageAppetite
    if (score !== null && score !== undefined) return score
  }

  // Check in-progress assessments
  if (activeAssessments.length > 0) {
    const sorted = [...activeAssessments].sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt))
    for (const assessment of sorted) {
      const ra = assessment.responses?.riskAppetite || {}
      const answered = Object.keys(ra).filter(key =>
        key.startsWith('ra-') && ra[key] !== null && ra[key] !== undefined
      )
      if (answered.length >= RISK_APPETITE_QUESTION_COUNT) {
        const values = answered.map(key => {
          const r = ra[key]
          return typeof r === 'object' ? r.value : r
        }).filter(v => typeof v === 'number')
        if (values.length >= RISK_APPETITE_QUESTION_COUNT) {
          return values.reduce((sum, val) => sum + val, 0) / values.length
        }
      }
    }
  }

  return null
}

// Client status styling
export const CLIENT_STATUS_OPTIONS = [
  { value: 'discovery', label: 'Discovery', color: 'bg-blue-100 text-blue-800 dark:bg-blue-900/40 dark:text-blue-300' },
  { value: 'retainer', label: 'Retainer', color: 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300' },
  { value: 'suspended', label: 'Suspended', color: 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-300' }
]
